import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Loader, AlertCircle, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ROLES = [
  { v: 'support', l: 'Support' },
  { v: 'finance', l: 'Finance' },
  { v: 'staff', l: 'Staff' },
  { v: 'admin', l: 'Admin' },
];

const StaffSignupPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', department: '',
    requested_role: 'staff', reason: '', invited_by: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.reason.trim().length < 10) { setError('Please describe why you need staff access (10+ characters).'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/signup/staff`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        department: form.department.trim() || null,
        requested_role: form.requested_role,
        reason: form.reason.trim(),
        invited_by: form.invited_by.trim() || null,
      });
      setSubmitted(true);
      toast.success('Request sent — pending owner/admin approval.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit request.');
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] p-6" data-testid="staff-signup-success">
        <div className="max-w-lg bg-white rounded-2xl border border-gray-200 p-10 text-center shadow-sm">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4"><Check className="h-6 w-6 text-emerald-700" /></div>
          <div className="text-2xl font-bold text-gray-900">Request submitted</div>
          <p className="text-sm text-gray-600 mt-3">Thanks {form.full_name.split(' ')[0]}. An owner or admin will review your request and respond by email. If approved, your follow-up email will include a temporary password and a link to the Winhouse Staff Hub.</p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/" className="h-10 px-5 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider flex items-center">← Winhouse Home</Link>
            <Link to="/staff/login" className="h-10 px-5 rounded-full bg-black text-white text-xs font-bold uppercase tracking-wider flex items-center">Staff Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#FAF9F6]" data-testid="staff-signup-page">
      <div className="lg:w-5/12 bg-black text-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div>
          <div className="text-[#D3D86F] text-xs uppercase tracking-[3px] font-bold mb-2">WINHOUSE STAFF HUB</div>
          <div className="text-3xl lg:text-4xl font-bold leading-tight">Request access</div>
          <div className="text-sm text-gray-300 mt-3 max-w-md">The Winhouse Staff Hub is private. Submit your details and an owner or admin will review your request.</div>
          <ul className="mt-8 space-y-2 text-sm text-gray-300">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#D3D86F] mt-0.5" /> Internal inbox + announcements.</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#D3D86F] mt-0.5" /> Role-based access — least privilege.</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#D3D86F] mt-0.5" /> Approval only by owner / admin.</li>
          </ul>
        </div>
        <div className="mt-10 lg:mt-0">
          <div className="flex items-center gap-2 text-[#D3D86F]"><Sparkles className="h-3 w-3" /><span className="text-[10px] uppercase tracking-[2px]">Owner · Admin · Support · Finance · Staff</span></div>
        </div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 rounded-full bg-[#D3D86F]/10 blur-3xl" />
      </div>

      <div className="lg:w-7/12 flex items-center justify-center p-8 lg:p-14">
        <form onSubmit={submit} className="w-full max-w-xl space-y-4" data-testid="staff-signup-form">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[2px] text-gray-500 font-bold">
            <ShieldCheck className="h-4 w-4 text-red-700" /> Staff access request
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name" v={form.full_name} on={(v) => u('full_name', v)} required testid="staff-signup-name" />
            <Field label="Email" type="email" v={form.email} on={(v) => u('email', v)} required testid="staff-signup-email" />
            <Field label="Phone (optional)" v={form.phone} on={(v) => u('phone', v)} testid="staff-signup-phone" />
            <Field label="Department (optional)" v={form.department} on={(v) => u('department', v)} testid="staff-signup-dept" hint="e.g. Marketing, Operations" />
            <div>
              <Label>Requested role</Label>
              <select value={form.requested_role} onChange={(e) => u('requested_role', e.target.value)} className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="staff-signup-role">
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </div>
            <Field label="Invited by (optional)" v={form.invited_by} on={(v) => u('invited_by', v)} testid="staff-signup-invited" hint="Email of the colleague who invited you" />
            <div className="sm:col-span-2">
              <Label>Reason for access</Label>
              <textarea required value={form.reason} onChange={(e) => u('reason', e.target.value)} rows={4} className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="staff-signup-reason" placeholder="Briefly explain what you need access to and why." />
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200" data-testid="staff-signup-error">
              <AlertCircle className="h-4 w-4 text-red-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-red-900">{error}</div>
            </div>
          )}

          <button type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50" data-testid="staff-signup-submit">
            {submitting ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {submitting ? 'Submitting…' : 'Submit request'}
          </button>
          <div className="text-[11px] text-gray-500 text-center">
            Already on the Winhouse team? <Link to="/staff/login" className="underline text-gray-700">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

const Label = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block">{children}</label>;
const Field = ({ label, v, on, type = 'text', required, testid, hint }) => (
  <div>
    <Label>{label}</Label>
    <input type={type} required={required} value={v} onChange={(e) => on(e.target.value)} className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid={testid} />
    {hint && <div className="text-[10px] text-gray-400 mt-1">{hint}</div>}
  </div>
);

export default StaffSignupPage;
